import { useCallback, useContext, useEffect, useState } from 'react';
import { getEndpoints, getRegionByPublicRegion } from '../../../utils/appConfig';
import { AppContext } from '../../../utils/appContext';
import { get } from 'lodash';
import { LogAction, LogOption } from './constants';
import { useExecuteCommandMutation } from './hooks/useExecuteCommandMutation';
import { Spinner } from '@amzn/awsui-components-react';

interface LogFetcherProps {
  initialLogFile: LogOption;
  onClose: () => void;
  publicDNS: string;
  action: LogAction;
  mechanicLogPath: string;
  fileLogPath?: string;
  suffixType?: string;
  domainIdentifier: string;
  instanceId: string;
}

export const LogFetcher = (props: LogFetcherProps) => {
  const {
    region: { publicRegion },
  } = useContext(AppContext);

  const appContext = useContext(AppContext);
  const stage = appContext.stage;

  const [getLogfile] = useExecuteCommandMutation();
  const [isLoading, setIsLoading] = useState(true);

  const getEnvLocations = useCallback(async () => {
    try {
      const resp = await getLogfile({
        variables: {
          url: 'aes/domain/dp/node/shell',
          args: {
            domain_identifier: props.domainIdentifier,
            base_cmd: 'find',
            args: '/apollo/env -type l -not -xtype l',
            instance_id: props.instanceId,
          },
        },
      });

      const responsePath: string = (typeof resp?.data?.executeCommand?.data === 'string')
        ? 'data.executeCommand.data'
        : 'data.executeCommand.data[0].stdout';

      const envLocations: string[] = get(resp, responsePath, '').split('\n');

      return {
        ES:
          envLocations.find((logLoc: string) => logLoc.indexOf('ES_') > -1) ||
          envLocations.find((logLoc: string) => logLoc.indexOf('OS_') > -1) ||
          envLocations.find((logLoc: string) => logLoc.indexOf('ESAMI') > -1) ||
          '/var/opt/opensearch/var/logs',
        Kibana:
          envLocations.find((logLoc: string) => logLoc.indexOf('Kibana') > -1) ||
          envLocations.find((logLoc: string) => logLoc.indexOf('OSD') > -1),
        ServiceProxy: envLocations.find(
          (logLoc: string) => logLoc.indexOf('ServiceProxy') > -1
        ),
        SDPDAgent: envLocations.find(
          (logLoc: string) => logLoc.indexOf('SDPDAgent') > -1
        ),
        LogPusher: envLocations.find(
          (logLoc: string) => logLoc.indexOf('LogPusher') > -1
        ),
        InstanceController: envLocations.find(
          (logLoc: string) => logLoc.indexOf('InstanceController') > -1
        ),
      };
    } catch (e) {
      console.error('Error fetching env locations:', e);
      throw e;
    }
  }, [props.domainIdentifier, props.instanceId, getLogfile]);

  useEffect(() => {
    const openMechanicUrl = async () => {
      setIsLoading(true);
      try {
        const region = getRegionByPublicRegion(publicRegion);

        if (!region) {
          console.error('Region not found:', publicRegion);
          return;
        }

        const envLocations = await getEnvLocations();
        const cluster = `${region.airportCode.toLowerCase()}prod`;

        // Containerized OpenSearch logs live at an absolute path
        // (e.g. /var/opt/opensearch/var/logs/<file>) rather than under an
        // /apollo/env alias, so getEnvLocations() returns that container path as
        // the fallback. The apollo-env log tools (and the apollo alias) don't
        // apply there -- fetch via the file-based tool with an absolute path.
        const envPath = envLocations[props.initialLogFile.env];
        const isContainerEnv =
          typeof envPath === 'string' && envPath.includes('/var/opt/opensearch');
        const effectiveLogPathType = isContainerEnv ? 'fileLog' : props.mechanicLogPath;

        const baseParameters: Record<string, any> = {
          host: [props.publicDNS],
          ...(effectiveLogPathType === 'fileLog'
              ? { path: isContainerEnv ? `${envPath}/${props.initialLogFile.value}` : props.fileLogPath }
              : { [props.action.id === 'downloadLog' ? 'name' : 'prefix']: props.initialLogFile.value }
          ),
          // suffix_type selects the hourly-rotated file under an apollo env; it is
          // meaningless for an absolute container path, so only send it on the
          // apollo path.
          ...(!isContainerEnv && props.suffixType && { suffix_type: props.suffixType })
        };

        if (effectiveLogPathType === 'apolloLog') {
          const alias = envPath;
          if (alias) {
            baseParameters.alias = alias.split('/').pop();
          }
        }

        // Build toolParameters in the Mechanic Shortcuts format
        const toolParameters: Record<string, any> = {};
        for (const [key, val] of Object.entries(baseParameters)) {
          toolParameters[key] = { value: val };
        }

        const toolPath =
          props.action.paths[
            effectiveLogPathType as keyof typeof props.action.paths
          ];

        const shortcutConfig = {
          metadata: {
            title: `Tumbler Live Log - ${props.initialLogFile.value}`,
            version: 1.1,
          },
          clientConfig: {
            stack: 'prod',
          },
          executionConfig: {
            executionParameters: {
              namespace: 'host',
              cluster: { name: cluster },
              operatorGroup: 'og-f81485a8-fd5d-4432-bf90-8f4372a33fd0',
              toolPath: toolPath,
              toolParameters: toolParameters,
            },
            clientParameters: {},
          },
        };

        const encodedConfig = btoa(JSON.stringify(shortcutConfig));
        const url = `${getEndpoints().mechanic}/execute?shortcut=${encodedConfig}`;
        window.open(url, '_blank');
      } catch (error) {
        console.error('Error opening mechanic URL:', error);
      } finally {
        setIsLoading(false);
        props.onClose();
      }
    };

    openMechanicUrl();
  }, [
    props.initialLogFile,
    props.publicDNS,
    props.mechanicLogPath,
    props.fileLogPath,
    props.action,
    publicRegion,
    props.suffixType,
    getEnvLocations,
    props.onClose
  ]);

  return isLoading ? (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.4)',
        zIndex: 9999
      }}
    >
      <Spinner size="large" />
      <div style={{ color: 'white', marginTop: '10px' }}>
        Opening mechanic...
      </div>
    </div>
  ) : null;
};